import { Controller } from "@hotwired/stimulus"

// Client-side lane controls for the three-lane inbox (docs/design/Inbox.dc.html):
//   * focus/expand — maximise one lane, collapse the others to slim rails
//   * sort — reorder the Respond lane ("Longest waiting" ↔ "Newest")
//
// The ordering rule is Core::Leads::RespondLaneOrder, and the server has already
// applied it to the list it served. This mirrors it so the toggle can reverse the
// clock without a round-trip — and so clicking back to "Longest waiting" restores
// exactly the order the page arrived in, rather than an order nothing ever showed.
//
// Cards carry the two inputs it needs: data-lead-state for the group and
// data-state-minutes for the clock, both the same values the card face displays.
export default class extends Controller {
  static targets = ["respondList", "respondSortLabel"]

  connect() {
    this.respondSort = "waiting" // longest waiting first
    // Applied, not just recorded. The list is served in this order so this is a
    // no-op on a fresh render — and that is the point: the control's label and
    // the list have to agree from first paint. Setting the field without calling
    // this left the button reading "Longest waiting" over a list in
    // priority-score order, and it took two clicks to reach the advertised order
    // (KI-221).
    this.sortRespond()
  }

  // Toggle maximise for the clicked lane. Re-clicking the focused lane restores
  // the three-up view; the collapsed rails also call this to restore.
  focus(event) {
    const lane = event.params.lane || event.currentTarget.dataset.lane
    const current = this.element.dataset.focused
    if (current === lane) {
      delete this.element.dataset.focused
    } else {
      this.element.dataset.focused = lane
    }
  }

  toggleSort() {
    this.respondSort = this.respondSort === "waiting" ? "newest" : "waiting"
    if (this.hasRespondSortLabelTarget) {
      this.respondSortLabelTarget.textContent =
        this.respondSort === "waiting" ? "Longest waiting" : "Newest"
    }
    this.sortRespond()
  }

  sortRespond() {
    if (!this.hasRespondListTarget) return

    const cards = Array.from(
      this.respondListTarget.querySelectorAll("[data-respond-card]")
    )
    if (cards.length < 2) return

    const longestFirst = this.respondSort === "waiting"
    cards
      .map((card, index) => ({ card, index }))
      .sort((a, b) => {
        const group = this.stateGroup(a.card) - this.stateGroup(b.card)
        if (group !== 0) return group

        const clock =
          this.clockRank(a.card, longestFirst) - this.clockRank(b.card, longestFirst)
        // Served order settles remaining ties, so the sort is stable and
        // deterministic — the same guarantee RespondLaneOrder gives the server.
        return clock !== 0 ? clock : a.index - b.index
      })
      .forEach(({ card }) => this.respondListTarget.appendChild(card))
  }

  // Work owed outranks work already sent, in BOTH directions of the toggle.
  // Grouping is not a sort direction; the toggle only reverses the clock.
  stateGroup(card) {
    return card.dataset.leadState === "waiting" ? 1 : 0
  }

  // No clock (the -1 sentinel, or a missing attribute) sorts last within its
  // group either way, because +Infinity is the largest key in both directions.
  clockRank(card, longestFirst) {
    const minutes = parseInt(card.dataset.stateMinutes, 10)
    if (Number.isNaN(minutes) || minutes < 0) return Number.POSITIVE_INFINITY

    return longestFirst ? -minutes : minutes
  }
}
